import { UIIcon } from '../../../config/icons';
import { FPageBlock, FPageInfoItem, FPageInfoKind } from '../../mdl/f-page-block';
import { UIInputAdapter } from '../../ui/component/adapter';
import { UILabeled } from '../../ui/component/labeled';
import { UIList } from '../../ui/component/list';
import { UISelect } from '../../ui/component/select';
import { UICheckbox } from '../../ui/component/toggle';
import { $el } from '../../ui/elements';
import { uiSetPrefixedClass } from '../../ui/functions';
import { UIValue } from '../../ui/types';
import { UIView, UIViewSpec } from '../../ui/view';
import { xavis_experiments } from '../../y/debug';
import { $on, YEventHandlers, YInputEvent, YStateEvent, YValueEvent } from '../../y/events';
import { $form, YInput } from '../../y/form';
import { EDBlock, EDBlockSpec } from '../block';
import { EDBlockContainer, EDButtonSpec, EDObject } from '../editor';
import { EDImageObject, emptyImage } from '../image';
import { EDI18NInput } from '../input';
import { $page_values } from './values';

class EditorInfoItem extends UIView implements EDObject, YInput<FPageInfoItem> {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<FPageInfoItem>>;

    doRemove?: () => void;
    properties: UIViewSpec;
    parent: EDBlock;
    private _has_image: UICheckbox;
    private _image: EDImageObject;
    private _text: EDI18NInput;
    private _extend: UICheckbox;
    private _flip: UICheckbox;
    okay = true;
    dirty = false;
    disabled = false;
    name = 'Item';

    constructor(block: EDBlock, kind: FPageInfoKind) {
        const image = new EDImageObject(block, 'page', undefined, true, 'tl-image r-image');
        const text = new EDI18NInput({ multiline: true, styleable: true, classes: 'r-text', formats: $page_values.default_formats, default_format: 'text' });

        super('div', ['ed-object', kind == 'genres' ? 'o-info-genre' : 'o-info-item'], [image, text]);
        this.ui_elem.ed_object = this;
        this.parent = block;

        this.root.addEventListener('click', e => {
            block.editor.selectObject(this);
            e.stopPropagation();
        });

        this._image = image;
        this._text = text;

        const has_image = this._has_image = new UICheckbox({ title: 'Has Image' });
        const extend = this._extend = new UICheckbox({ title: 'Extend' });
        const flip = this._flip = new UICheckbox({ title: 'Flip' });

        this.properties = $el.DIV('l-form', [
            has_image,
            extend,
            flip,
            image.properties
        ]);

        $on(has_image, 'value', v => {
            image.ui_show(undefined, v);
            image.properties.ui_show(undefined, v);
        });

        $on(extend, 'value', v => {
            this.classList.toggle('m-extend', !!v);
        });

        $on(flip, 'value', v => {
            this.classList.toggle('m-flip', !!v);
        });

        image.ui_hide();
        image.properties.ui_hide();
    }

    get value() {
        return { image: this._has_image.value ? this._image.value : undefined, text: this._text.value, extend: this._extend.value, flip: this._flip.value };
    }

    set value(v: FPageInfoItem) {
        if(typeof v.image == 'string')
            v.image = undefined;
        this._has_image.value = v.image != undefined;
        this._image.value = v.image;
        this._text.value = v.text;
        this._extend.value = v.extend;
        this._flip.value = v.flip;
    }
}

const info_kind_values: UIValue<FPageInfoKind>[] = [{ key: 'default', name: 'Default' }, { key: 'phones', name: 'Phones' }, { key: 'vertical', name: 'Vertical' }, { key: 'genres', name: 'Genres' }];

export const ed_page_info_block: EDBlockSpec<FPageBlock> = {
    key: 'info',
    editor_block: class extends EDBlock {
        private _items: UIInputAdapter<FPageInfoItem, EditorInfoItem>;

        constructor(container: EDBlockContainer) {
            const kind = new UISelect(info_kind_values, { kind: 'k-subtle' });

            const items = new UIInputAdapter<FPageInfoItem, EditorInfoItem>((_, entry) => {
                const item: EditorInfoItem = new EditorInfoItem(this, kind.value!)
                item.doRemove = () => { items.remove(entry); };
                return { view: item, input: item };
            });

            const properties = $el.DIV('l-form', [
                xavis_experiments && new UILabeled(kind, { above: 'Display' }),
            ]);

            const list = new UIList<UIViewSpec>(items, { classes: ['blk-info', 'w-block', 'b', 'b-content'] });

            super($form({ items, kind }), container, 'Info', properties, list.ui_elem);

            this._items = items;

            $on(kind, 'value', v => {
                uiSetPrefixedClass(list, 'k', v || 'legacy');

                for(const view of this._items.views) {
                    view.classList.toggle('o-info-item', v != 'genres');
                    view.classList.toggle('o-info-genre', v == 'genres');
                }
            });
        }

        override getButtons(): EDButtonSpec[] {
            const rect = this.ui_elem.getBoundingClientRect();

            return [{
                icon: UIIcon.PLUS,
                classes: 'm-square',
                action: () => {
                    this._items.push({ text: {}, image: { all: emptyImage() } });
                },
                position: [rect.right - 15, rect.bottom - 15],
                tooltip: 'Add item'
            }, ...this.parent.getChildButtons(this)];
        }

        get children() {
            return this._items.views;
        }
    },
};
